import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  ChevronDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LanguageSwitcher from './LanguageSwitcher';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const [showContacts, setShowContacts] = useState(false);
  const [profilePicture, setProfilePicture] = useState('/profile-jean-bilbao.jpeg');

  useEffect(() => {
    const loadProfilePicture = async () => {
      try {
        const response = await fetch('/api/admin/profile');
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && result.data.profilePicture) {
            setProfilePicture(result.data.profilePicture);
          }
        }
      } catch (error) {
        console.error('Error loading profile picture:', error);
        // Keep default image on error
      }
    };

    loadProfilePicture();
  }, []);

  return (
    <aside className="bg-gray-800 border border-gray-700 rounded-2xl p-6 lg:p-8 h-full lg:h-auto">
      {/* Close button for mobile */}
      {onClose && (
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      )}

      {/* Profile Section */}
      <div className="text-center mb-8">
        {/* Avatar */}
        <div className="relative mb-6">
          <div className="w-24 h-24 mx-auto rounded-2xl overflow-hidden bg-gradient-to-br from-primary-400 to-primary-600 p-1">
            <img
              src={profilePicture}
              alt="Jean Bilbao"
              className="w-full h-full object-cover rounded-xl"
              loading="lazy"
            />
          </div>
        </div>

        {/* Name and Title */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white mb-2">
            {t('hero.name')}
          </h1>
          <p className="text-sm text-gray-300 bg-gray-700 px-3 py-1 rounded-lg inline-block">
            {t('hero.title')}
          </p>
        </div>

        {/* Show Contacts Button */}
        <button
          onClick={() => setShowContacts(!showContacts)}
          className="flex items-center justify-center gap-2 text-sm text-gray-300 hover:text-white transition-colors mx-auto"
        >
          <span>{t('hero.showContacts')}</span>
          <ChevronDownIcon 
            className={`w-4 h-4 transition-transform duration-200 ${
              showContacts ? 'rotate-180' : ''
            }`} 
          />
        </button>
      </div>

      {/* Contact Information */}
      <div className={`transition-all duration-300 overflow-hidden ${
        showContacts ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="border-t border-gray-700 pt-6 mb-6">
          <ul className="space-y-4">
            {/* Email */}
            <li className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <EnvelopeIcon className="w-4 h-4 text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                  {t('contact.email')}
                </p>
                <a 
                  href="mailto:jean.bilbao@outlook.com"
                  className="text-sm text-white hover:text-primary-400 transition-colors break-all"
                >
                  jean.bilbao@outlook.com
                </a>
              </div>
            </li>

            {/* Phone */}
            <li className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <PhoneIcon className="w-4 h-4 text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                  {t('contact.phone')}
                </p>
                <a 
                  href="https://wa.link/kapkcp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white hover:text-primary-400 transition-colors"
                >
                  +32 470058254
                </a>
              </div>
            </li>

            {/* Location */}
            <li className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center">
                <MapPinIcon className="w-4 h-4 text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                  {t('contact.location')}
                </p>
                <address className="text-sm text-white not-italic">
                  {t('contact.address')}
                </address>
              </div>
            </li>
          </ul>
        </div>

        {/* CTA Button */}
        <div className="border-t border-gray-700 pt-6 mb-6">
          <button 
            className="w-full btn-primary text-sm py-3"
            onClick={() => {
              navigate(`/${lang || 'en'}/consultation`);
              if (onClose) onClose();
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4l-2 16h14l-2-16" />
              </svg>
              {t('hero.cta')}
            </span>
          </button>
        </div>

        {/* Social Links */}
        <div className="border-t border-gray-700 pt-6">
          <div className="flex justify-center">
            <a
              href="https://www.linkedin.com/in/jeanbilbao/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gray-700 hover:bg-primary-500 rounded-lg flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Language Switcher - Desktop */}
      <div className="hidden lg:block mt-8 pt-6 border-t border-gray-700">
        <LanguageSwitcher />
      </div>
    </aside>
  );
};

export default Sidebar;